import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQueryWithAuth } from "services/common/rtkQueryConfig";

export interface NotificationResponse {
  id: string;
  userId: string;
  nhDealApplicationId: string;
  title: string;
  text: string;
  link: string;
  createdOn: Date;
  notificationType?: NotificationType;
  isEsignViewed?: boolean;
}
export interface Consent {
  userId: string;
  nhDealApplicationId: string;
  prospectId?: string;
  consentTypes: ConsentTypes[];
}
export enum ConsentTypes {
  Econsent = "Econsent",
  PrivacyPolicy = "PrivacyPolicy",
  CreditConsent = "CreditConsent",
  DataSharing = "DataSharing",
  PhoneConsent = "PhoneConsent",
  TermsOfUse = "TermsOfUse",
  EmailConsent = "EmailConsent",
}
export enum NotificationType {
  Esign = "Esign",
  LoanDetails = "LoanDetails",
  PAL = "PAL",
  Uqual = "Uqual",
}

export interface LoanOfficerAdStatus {
  isEligible: boolean;
  email?: string;
}

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithAuth, //baseQuery
  endpoints: (build) => ({
    getNotificationDetails: build.query<NotificationResponse[], string>({
      query: (userId: string) => ({
        url: `/notification/user/${userId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0,
    }),
    setStatusFlagOfNotification: build.mutation<void, string>({
      query: (id: string) => ({
        url: `/notification/${id}/isViewed-flag`,
        method: "PATCH",
      }),
    }),
    setEsignStatusFlagOfNotification: build.mutation<void, string>({
      query: (id: string) => ({
        url: `/notification/${id}/esign-is-viewed-flag`,
        method: "PATCH",
      }),
    }),
    setConsent: build.mutation<void, { id: string; consent: Consent }>({
      query: ({ id, consent }) => ({
        url: `/compliance/nh-deal-application-id/${id}/consent`,
        method: "POST",
        body: consent,
      }),
    }),
    setRedesignAnnouncement: build.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/user/${userId}/re-design-announcement`,
        method: "PATCH",
      }),
    }),
    getAdEligibility: build.query<boolean, string>({
      query: (userId) => ({
        url: `/user/${userId}/ad-eligibility`,
        method: "GET",
      })
    }),
    getLoAdEligibility: build.query<LoanOfficerAdStatus, string>({
      query: (userId) => ({
        url: `/user/${userId}/lo-ad-eligibility`,
        method: "GET",
      })
    }),
    updateAdEligibility: build.mutation<void, { userId: string; eligible: boolean }>({
      query: ({ userId, eligible }) => ({
        url: `/user/${userId}/ad-eligibility`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(eligible),
      })
    }),
    syncPreferredLanguage: build.mutation<
      void,
      { userId: string; preferredLanguage: string; nhDealApplicationId?: string }
    >({
      query: ({ userId, preferredLanguage, nhDealApplicationId }) => ({
        url: `/user/${userId}/preferred-language/deal-sync`,
        method: "PATCH",
        body: {
          preferredLanguage,
          ...(nhDealApplicationId ? { nhDealApplicationId } : {}),
        },
      }),
    }),
  }),
});

export const {
  useGetNotificationDetailsQuery,
  useSetStatusFlagOfNotificationMutation,
  useSetEsignStatusFlagOfNotificationMutation,
  useSetConsentMutation,
  useSetRedesignAnnouncementMutation,
  useGetAdEligibilityQuery,
  useUpdateAdEligibilityMutation,
  useGetLoAdEligibilityQuery,
  useSyncPreferredLanguageMutation,
} = userApi;
